import { useEffect, useState } from "react";
import { Button } from "zudoku/components";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "zudoku/ui/Dialog";
import { Input } from "zudoku/ui/Input";
import { Label } from "zudoku/ui/Label";
import { Textarea } from "zudoku/ui/Textarea";

export interface AppFormValues {
	name: string;
	description?: string | null;
	privacyUrl?: string | null;
}

interface CreateAppDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSubmit: (values: AppFormValues) => Promise<void> | void;
	isSubmitting?: boolean;
	initialValues?: AppFormValues;
	title?: string;
	description?: string;
	ctaLabel?: string;
	readOnlyName?: boolean;
}

export const CreateAppDialog = ({
	open,
	onOpenChange,
	onSubmit,
	isSubmitting,
	initialValues,
	title = "Create App",
	description = "Provide a name, privacy policy URL, and optional description for the app.",
	ctaLabel = "Create",
	readOnlyName,
}: CreateAppDialogProps) => {
	const [name, setName] = useState(initialValues?.name ?? "");
	const [appDescription, setAppDescription] = useState(
		initialValues?.description ?? "",
	);
	const [privacyUrl, setPrivacyUrl] = useState(initialValues?.privacyUrl ?? "");

	useEffect(() => {
		if (open) {
			setName(initialValues?.name ?? "");
			setAppDescription(initialValues?.description ?? "");
			setPrivacyUrl(initialValues?.privacyUrl ?? "");
		}
	}, [
		initialValues?.description,
		initialValues?.name,
		initialValues?.privacyUrl,
		open,
	]);

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		await onSubmit({
			name: name.trim(),
			description: appDescription?.trim() || undefined,
			privacyUrl: privacyUrl?.trim() || undefined,
		});
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<form onSubmit={handleSubmit} className="space-y-4">
					<DialogHeader>
						<DialogTitle>{title}</DialogTitle>
						{description ? (
							<DialogDescription>{description}</DialogDescription>
						) : null}
					</DialogHeader>

					<div className="space-y-2">
						<Label htmlFor="app-name">App Name</Label>
						<Input
							id="app-name"
							required
							disabled={readOnlyName}
							placeholder="Vehicle Insights"
							value={name}
							onChange={(event) => setName(event.target.value)}
						/>
					</div>

					<div className="space-y-2">
						<Label htmlFor="app-privacy-url">Privacy URL</Label>
						<Input
							id="app-privacy-url"
							type="url"
							placeholder="https://example.com/privacy"
							value={privacyUrl ?? ""}
							onChange={(event) => setPrivacyUrl(event.target.value)}
						/>
					</div>

					<div className="space-y-2">
						<Label htmlFor="app-description">Description</Label>
						<Textarea
							id="app-description"
							placeholder="Describe how this app uses the API (optional)"
							value={appDescription ?? ""}
							onChange={(event) => setAppDescription(event.target.value)}
						/>
					</div>

					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => onOpenChange(false)}
							disabled={isSubmitting}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={!name.trim() || isSubmitting}>
							{isSubmitting ? "Saving..." : ctaLabel}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
};
